import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography } from "@mui/material";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import ConfirmDialog from "../components/common/ConfirmDialog";
import RowActionMenu, {
  RowActionDef,
} from "../components/common/RowActionMenu";

import CreateTimetableJobModal from "../components/timetables/CreateTimetableJobModal";
import JobProgressModal from "../components/timetables/JobProgressModal";
import UpdateTimetableModal from "../components/timetables/UpdateTimetableModal";
import ViewTimetableModal from "../components/timetables/ViewTimetableModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getTimetables,
  getTimetableById,
  deleteTimetable,
  resetTimetableServerStatus,
} from "../redux/actions/timetableActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { getTerms } from "../redux/actions/termActions";
import { getClassrooms } from "../redux/actions/classroomActions";

import { TimetableSummaryDto } from "../shared/dtos/timetable/TimetableSummaryDto";
import { TimetableStatus } from "../shared/enums/TimetableStatus";
import AcademicYearSelectFilter from "../components/common/AcademicYearSelectFilter";
import TermSelectFilter from "../components/common/TermSelectFilter";
import ClassroomSelectFilter from "../components/common/ClassroomSelectFilter";
import { TIMETABLE_STATUS_OPTIONS } from "../shared/data/enumOptions";

const TimetablesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- Redux State ---
  const {
    timetables,
    loadingTimetables,
    errorLoadingTimetables,
    loadingTimetableUpdate,
    errorUpdatingTimetable,
    pagination,
    serverStatus,
    loadingTimetableDelete,
    timetable,
    loadingTimetable,
    errorLoadingTimetable,
  } = useAppSelector((state) => state.timetable);

  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );
  const { items: terms } = useAppSelector((state) => state.term);
  const { items: classrooms } = useAppSelector((state) => state.classroom);
  const { userInfo } = useAppSelector((state) => state.auth);

  const { loadingJobStart, errorStartingJob, activeJob } = useAppSelector(
    (state) => state.timetableJob,
  );

  // --- Local Filters ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce(searchTerm, 400);

  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedYearId, setSelectedYearId] = useState<string>("");
  const [selectedTermId, setSelectedTermId] = useState<string>("");
  const [selectedClassroomId, setSelectedClassroomId] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");

  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // Modal State
  const [isProgressModalOpen, setIsProgressModalOpen] =
    useState<boolean>(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedTimetable, setSelectedTimetable] =
    useState<TimetableSummaryDto | null>(null);

  const canCreate = hasPermission("timetable:create");
  const canEdit = hasPermission("timetable:update");
  const canDelete = hasPermission("timetable:delete");
  const canView = hasPermission("timetable:read");

  // Load lookups
  useEffect(() => {
    if (!academicYears.length) dispatch(getAcademicYears());
    if (!terms.length) dispatch(getTerms());
    if (!classrooms.length) dispatch(getClassrooms());
  }, [dispatch, academicYears.length, terms.length, classrooms.length]);

  // Fetch list with combined filters
  const fetchTimetablesList = useCallback((): void => {
    dispatch(
      getTimetables({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
        academicYearId: selectedYearId || undefined,
        termId: selectedTermId || undefined,
        classroomId: selectedClassroomId || undefined,
        status: (selectedStatus as TimetableStatus) || undefined,
      }),
    );
  }, [
    dispatch,
    pageConfig,
    debouncedSearch,
    selectedBranchId,
    selectedYearId,
    selectedTermId,
    selectedClassroomId,
    selectedStatus,
  ]);

  useEffect(() => {
    fetchTimetablesList();
  }, [fetchTimetablesList]);

  // Switch to progress modal when job starts
  useEffect(() => {
    if (activeJob && !isProgressModalOpen) {
      setIsCreateModalOpen(false);
      setIsProgressModalOpen(true);
    }
  }, [activeJob, isProgressModalOpen]);

  const handleCloseProgress = (success: boolean): void => {
    setIsProgressModalOpen(false);
    if (success) fetchTimetablesList();
  };

  const handleOpenView = (row: TimetableSummaryDto): void => {
    dispatch(getTimetableById(row.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleOpenUpdate = (row: TimetableSummaryDto): void => {
    setSelectedTimetable(row);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingTimetableUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedTimetable(null);
    }
  }, [loadingTimetableUpdate]);

  const handleOpenDelete = (row: TimetableSummaryDto): void => {
    setSelectedTimetable(row);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingTimetableDelete) {
      setIsDeleteModalOpen(false);
      setSelectedTimetable(null);
    }
  }, [loadingTimetableDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedTimetable) {
      dispatch(deleteTimetable(selectedTimetable.id));
    }
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetTimetableServerStatus());
  }, [serverStatus, dispatch, handleCloseDelete, handleCloseUpdate]);

  const columns = useMemo<TableColumn<TimetableSummaryDto>[]>(
    () => [
      {
        id: "name",
        label: "Timetable Name",
        minWidth: 200,
        format: (_, row) => (
          <Typography component="span" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
        ),
      },
      { id: "classroomName", label: "Classroom", minWidth: 120 },
      { id: "gradeLevelName", label: "Grade", minWidth: 100 },
      { id: "termName", label: "Term", minWidth: 100 },
      {
        id: "status",
        label: "Status",
        minWidth: 100,
        format: (value) => <StatusChip status={value as any} />,
      },
      {
        id: "createdAt",
        label: "Generated On",
        minWidth: 150,
        format: (value) => new Date(value as string).toLocaleDateString(),
      },
    ],
    [],
  );

  const renderRowActions = (row: TimetableSummaryDto): React.ReactNode => {
    const inlineActions: RowActionDef[] = [
      {
        label: "Details",
        icon: <VisibilityOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: () => handleOpenView(row),
        show: canView,
      },
    ];

    const menuActions: RowActionDef[] = [
      {
        label: "Edit Status",
        icon: <EditOutlinedIcon fontSize="small" />,
        onClick: () => handleOpenUpdate(row),
        show: canEdit,
      },
      {
        label: "Delete Timetable",
        icon: <DeleteOutlineIcon fontSize="small" />,
        color: "error",
        onClick: () => handleOpenDelete(row),
        show: canDelete,
      },
    ];

    return (
      <RowActionMenu inlineActions={inlineActions} menuActions={menuActions} />
    );
  };

  return (
    <PageContainer
      title="Timetables"
      subtitle="Manage and view auto-generated class schedules."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AutoModeIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Generate New
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((p) => ({ ...p, page: 1 }));
        }}
        searchPlaceholder="Search timetables..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => {
            setSelectedBranchId(bId);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />

        <AcademicYearSelectFilter
          value={selectedYearId}
          onChange={(val) => {
            setSelectedYearId(val);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />

        <TermSelectFilter
          value={selectedTermId}
          onChange={(val) => {
            setSelectedTermId(val);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />

        <ClassroomSelectFilter
          value={selectedClassroomId}
          onChange={(val) => {
            setSelectedClassroomId(val);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />

        <AppSelectFilter
          label="Status"
          value={selectedStatus}
          onChange={(val) => {
            setSelectedStatus(val);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
          options={TIMETABLE_STATUS_OPTIONS}
          allLabel="All Statuses"
          minWidth={140}
        />
      </PageToolbar>

      <StandardTable<TimetableSummaryDto>
        columns={columns}
        data={timetables}
        loading={loadingTimetables}
        error={errorLoadingTimetables}
        onRetry={fetchTimetablesList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(newPage) =>
          setPageConfig((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(newLimit) =>
          setPageConfig((prev) => ({ ...prev, limit: newLimit, page: 1 }))
        }
        rowAction={renderRowActions}
      />

      {isCreateModalOpen && (
        <CreateTimetableJobModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          userInfo={userInfo}
          loading={loadingJobStart}
          error={errorStartingJob}
        />
      )}

      {isProgressModalOpen && (
        <JobProgressModal
          open={isProgressModalOpen}
          onClose={handleCloseProgress}
        />
      )}

      {isUpdateModalOpen && selectedTimetable && (
        <UpdateTimetableModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          timetable={selectedTimetable}
          loading={loadingTimetableUpdate}
          error={errorUpdatingTimetable}
        />
      )}

      {isViewModalOpen && (
        <ViewTimetableModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          timetable={timetable}
          loading={loadingTimetable}
          error={errorLoadingTimetable}
        />
      )}

      {isDeleteModalOpen && selectedTimetable && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Timetable"
          description={`Are you sure you want to delete "${selectedTimetable.name}"? This will remove all associated lessons.`}
          loading={loadingTimetableDelete}
        />
      )}
    </PageContainer>
  );
};

export default TimetablesPage;
